import React from "react";
import { useSeo } from "../hooks/useSeo";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <section className="mb-12">
    <h2 className="font-['Oswald'] text-2xl font-bold text-[#D4A537] mb-4">{title}</h2>
    <div className="text-[#A8A091] leading-relaxed space-y-3">{children}</div>
  </section>
);

const LegalLayout = ({ testId, label, title, path, description, children }) => {
  useSeo(title, description, path);
  return (
    <main data-testid={testId} className="pt-20">
      <section className="bg-[#0A0A0A] py-20 border-b border-[#2E2820]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="text-[#D4A537] font-semibold uppercase tracking-wider text-sm">{label}</span>
          <h1 className="font-['Oswald'] text-4xl sm:text-5xl font-bold text-white mt-4">{title}</h1>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">{children}</div>
    </main>
  );
};

const Strong = ({ children }) => <strong className="text-[#F5EFE3] font-semibold">{children}</strong>;

export const MentionsLegales = () => (
  <LegalLayout testId="mentions-legales-page" label="Informations légales" title="Mentions légales" path="/mentions-legales" description="Mentions légales du site acelec94.com, édité par A.C. ELEC 94, SARL, 60 rue François Iᵉʳ 75008 Paris.">
    <Section title="Éditeur du site">
      <p>Le site <Strong>acelec94.com</Strong> est édité par :</p>
      <ul className="space-y-1" data-testid="legal-editor-info">
        <li><Strong>Dénomination :</Strong> A.C. ELEC 94</li>
        <li><Strong>Forme juridique :</Strong> Société à responsabilité limitée (SARL)</li>
        <li><Strong>Siège social :</Strong> 60 rue François Iᵉʳ, 75008 Paris</li>
        <li><Strong>SIREN :</Strong> 943 837 419 — RCS Paris</li>
        <li><Strong>SIRET :</Strong> 943 837 419 00010</li>
        <li><Strong>N° TVA intracommunautaire :</Strong> FR13943837419</li>
        <li><Strong>Code APE / NAF :</Strong> 43.21A — Travaux d'installation électrique dans tous locaux</li>
        <li><Strong>Téléphone :</Strong> 06 64 18 92 91 / 06 69 25 83 50</li>
        <li><Strong>Email :</Strong> contact@acelec94.com</li>
      </ul>
    </Section>
    <Section title="Directeur de la publication">
      <p>Madame Claudia-Mădălina Pătraşcu, en qualité de Gérante.</p>
    </Section>
    <Section title="Hébergement">
      <p>Le site est hébergé par <Strong>Emergent Labs</Strong> (emergent.sh).</p>
      <p>Le nom de domaine est enregistré auprès de <Strong>IONOS SARL</Strong>, 7 place de la Gare, 57200 Sarreguemines, France.</p>
    </Section>
    <Section title="Propriété intellectuelle">
      <p>L'ensemble des contenus de ce site (textes, photographies de chantiers, logo, enseigne, mise en page) est la propriété exclusive d'A.C. ELEC 94, sauf mention contraire. Toute reproduction, représentation ou diffusion, totale ou partielle, sans autorisation écrite préalable est interdite.</p>
      <p>Les noms des clients et donneurs d'ordre cités dans la rubrique Réalisations restent la propriété de leurs titulaires respectifs.</p>
    </Section>
    <Section title="Responsabilité">
      <p>A.C. ELEC 94 s'efforce de fournir des informations exactes et à jour. Les périodes de chantiers indiquées le sont à titre informatif. A.C. ELEC 94 ne saurait être tenue responsable d'erreurs, d'omissions ou d'une indisponibilité temporaire du site.</p>
    </Section>
    <Section title="Données personnelles">
      <p>Le traitement des données transmises via le formulaire de contact est décrit dans notre <Link to="/politique-de-confidentialite" data-testid="legal-to-privacy-link" className="text-[#D4A537] hover:underline">politique de confidentialité</Link>.</p>
    </Section>
    <Section title="Droit applicable">
      <p>Les présentes mentions légales sont régies par le droit français. En cas de litige, les tribunaux de Paris sont seuls compétents.</p>
    </Section>
  </LegalLayout>
);

export const PolitiqueConfidentialite = () => (
  <LegalLayout testId="politique-confidentialite-page" label="RGPD" title="Politique de confidentialité" path="/politique-de-confidentialite" description="Politique de confidentialité et traitement des données personnelles (RGPD) du site acelec94.com.">
    <Section title="Responsable du traitement">
      <p><Strong>A.C. ELEC 94</Strong>, SARL au siège situé 60 rue François Iᵉʳ, 75008 Paris (SIREN 943 837 419), représentée par sa Gérante, Madame Claudia-Mădălina Pătraşcu. Contact : contact@acelec94.com.</p>
    </Section>
    <Section title="Données collectées">
      <p>Via le formulaire de contact, nous collectons uniquement : nom, entreprise (facultatif), adresse email, numéro de téléphone, type de projet (facultatif) et message.</p>
      <p>Le site n'utilise ni cookies publicitaires, ni outil de suivi ou de mesure d'audience.</p>
    </Section>
    <Section title="Finalité et base légale">
      <p>Ces données servent exclusivement à répondre à votre demande de contact ou de devis et à assurer le suivi commercial de celle-ci. Le traitement repose sur des mesures précontractuelles prises à votre demande et sur notre intérêt légitime à répondre aux sollicitations professionnelles (article 6.1.b et 6.1.f du RGPD).</p>
    </Section>
    <Section title="Destinataires">
      <p>Les données sont destinées uniquement à A.C. ELEC 94. Elles ne sont jamais vendues ni cédées à des tiers. Nos prestataires techniques (hébergement du site, service d'envoi d'emails Resend) y accèdent uniquement pour assurer le fonctionnement du service.</p>
    </Section>
    <Section title="Durée de conservation">
      <p>Les données sont conservées au maximum <Strong>3 ans</Strong> à compter du dernier contact, puis supprimées, sauf si une relation contractuelle s'établit (les données sont alors conservées pendant la durée légale applicable).</p>
    </Section>
    <Section title="Sécurité">
      <p>Les échanges avec le site sont chiffrés (HTTPS). L'accès aux demandes reçues est réservé à A.C. ELEC 94, et le formulaire est protégé contre les envois automatisés abusifs.</p>
    </Section>
    <Section title="Vos droits">
      <p>Conformément au RGPD et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité de vos données.</p>
      <p>Pour exercer ces droits, écrivez à <a href="mailto:contact@acelec94.com" data-testid="privacy-email-link" className="text-[#D4A537] hover:underline">contact@acelec94.com</a>. Une réponse vous sera apportée sous un mois.</p>
      <p>Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).</p>
    </Section>
    <Section title="Mise à jour">
      <p>Cette politique peut être modifiée à tout moment. Dernière mise à jour : juin 2026.</p>
    </Section>
  </LegalLayout>
);
